<template>
  <div class="upload-photo">
    <form
      method="post"
      ref="photoForm"
      v-if="uploadHeaders"
    >
      <input
        type="file"
        :key="timeStamp"
        accept="image/*"
        @change="addFile"
        :ref="'popup-select-files'"
        autocomplete="off"
      >
      <slot />
    </form>
  </div>
</template>

<script>
import { useBaseUrl } from '@/utils/http/hook'
import { FILE_TRANSFER_TIMEOUT } from '@/utils/consts'
import { mapGetters } from 'vuex'
import { SETTING_CODE } from '@/api/settings/consts'
import rotateImg from './rotateImg'
import httpHeaderMixin from './httpHeaderMixin'

export default {
  mixins: [httpHeaderMixin],
  props: {
    sourceId: {
      type: String,
      default: '',
      required: true,
    },
    action: String,
    targetItem: {
      type: Object,
      default: () => {},
    },
    additionalInfo: {
      type: Object,
      default: () => ({}),
    },
    uploadSuccess: {
      type: Function,
      default: () => {},
    },
    uploadError: {
      type: Function,
      default: () => {},
    },
    uploadProgress: {
      type: Function,
      default: () => {},
    },
    beforeUpload: {
      type: Function,
      default: () => { return true },
    },
    uploadTimeout: {
      type: Function,
      default: () => {},
    },
  },
  data () {
    return {
      baseURL: useBaseUrl(),
      file: '',
      uploadHeaders: '',
      timeoutInMilli: FILE_TRANSFER_TIMEOUT || 30000,
      timeStamp: new Date(),
    }
  },
  computed: {
    ...mapGetters('settings', {
      getSettingByCode: 'setting',
    }),
    docUploadSize () {
      return parseInt(this.getSettingByCode(SETTING_CODE.docUploadSize), 10) || 10
    },
  },
  async mounted () {
    this.uploadHeaders = await this.getHeaders()
  },
  methods: {
    addFile (file) {
      const photo = this.$refs['popup-select-files'].files[0]
      if (!photo) {
        return
      }
      if ((photo.size / 1000 / 1000) > this.docUploadSize) {
        this.uploadError(`File ${photo.name} upload error: max photo size is ${this.docUploadSize}MB.`)
        return false
      }
      this.file = photo
      let reader = new FileReader(this.$refs.photoForm)
      const self = this
      reader.onload = function (e) {
        rotateImg(e, photo).then((blob, orientation) => {
          const formData = new FormData()
          formData.append('file', blob, (new Date()).getTime() + '|NAME|' + photo.name)
          formData.append('srcfilename', photo.name)
          formData.append('src', self.sourceId)
          formData.append('orientation', orientation)
          if (Object.keys(self.additionalInfo)) {
            for (const key in self.additionalInfo) {
              formData.append(key, self.additionalInfo[key])
            }
          }
          if (!self.doUploadPhoto(formData)) {
            self.resetInput()
          }
        })
      }
      reader.readAsDataURL(photo)
    },

    async getHeaders () {
      let headers = {
        'Accept': 'application/json',
        'Cache-Control': 'no-cache',
        'X-Requested-With': 'XMLHttpRequest',
        'X-Content-Type-Options': 'nosniff',
      }
      if (this.action) {
        headers = { ...headers, 'X-CMS-Action': this.action }
      }
      return this.getHttpHeaders(headers)
    },
    doUploadPhoto (formData) {
      const xhr = new XMLHttpRequest()
      if (!this.beforeUpload(xhr, this.file)) {
        return false
      }

      xhr.open('POST', `${this.baseURL}/docs/image`, true)
      for (const key in this.uploadHeaders) {
        xhr.setRequestHeader(key, this.uploadHeaders[key])
      }
      xhr.timeout = this.timeoutInMilli
      xhr.ontimeout = this.onUploadTimeout
      xhr.onload = this.photoUploading
      xhr.onerror = this.uploadError
      xhr.onprogress = this.uploadProgress
      xhr.send(formData)
      return true
    },
    photoUploading (e) {
      const xhr = e.target
      let error = ''
      let response = ''

      if (xhr.readyState !== 4) {
        return
      }
      if (xhr.responseType !== 'arraybuffer' && xhr.responseType !== 'blob') {
        if (xhr.getResponseHeader('content-type') && ~xhr.getResponseHeader('content-type').indexOf('application/json')) {
          try {
            response = JSON.parse(xhr.response)
          } catch (_error) {
            error = _error
            response = 'Invalid JSON response from server.'
          }
        }
      }
      this.resetInput()
      if (xhr.status >= 200 && xhr.status < 300) {
        return this.uploadSuccess(response.data)
      } else {
        return this.uploadError(error)
      }
    },
    onUploadTimeout (e) {
      this.uploadTimeout(e, this.file)
    },
    popupSelectFile () {
      this.$refs['popup-select-files'].click()
    },
    resetInput () {
      this.$refs['popup-select-files'].value = ''
      this.timeStamp = new Date()
    },
  },
}
</script>

<style lang="scss" scoped>
  .upload-photo {
    position: absolute;
    top: 0;
    bottom: 0;
    right: 0;
    left: 0;
    form, input{
      opacity: 0;
      width: 0;
      height: 0;
    }
  }
</style>
